import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { inputClass } from "../../constants/formStyles";

export interface Option {
  value: string;
  text: string;
  selected: boolean;
  element?: HTMLElement;
}

interface DropdownProps {
  options: Option[];
  setOptions: Dispatch<SetStateAction<Option[]>>;
  id: string;
  placeholder: string;
  onSelectionChange: (selectedValues: string[]) => void;
}

interface DropdownPos {
  top: number;
  left: number;
  width: number;
}

const MultiSelect: React.FC<DropdownProps> = ({
  id,
  options,
  setOptions,
  placeholder,
  onSelectionChange,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [show, setShow] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPos, setDropdownPos] = useState<DropdownPos>({
    top: 0,
    left: 0,
    width: 0,
  });

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = searchQuery
    ? options.filter((o) =>
        o.text.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const allSelected = options.length > 0 && selected.length === options.length;

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
  };

  useLayoutEffect(() => {
    if (!show) return;
    updatePosition();
  }, [show, selected]);

  useEffect(() => {
    if (!show) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [show]);

  useEffect(() => {
    setSelected(
      options
        .map((o) => (o.selected ? o.value : null))
        .filter((v): v is string => v !== null)
    );
  }, [options]);

  useEffect(() => {
    const handler = ({ target }: MouseEvent) => {
      if (
        show &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target as Node) &&
        !triggerRef.current?.contains(target as Node)
      )
        setShow(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  });

  const notify = (newSelected: string[], newOptions: Option[]) => {
    setSelected(newSelected);
    setOptions(newOptions);
    onSelectionChange(newSelected);
  };

  const toggle = (index: number) => {
    const newOptions = options.map((o, i) =>
      i === index ? { ...o, selected: !o.selected } : o
    );
    const newSelected = newOptions
      .filter((o) => o.selected)
      .map((o) => o.value);
    notify(newSelected, newOptions);
  };

  const remove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    toggle(index);
  };

  const toggleSelectAll = () => {
    const selecting = !allSelected;
    const newOptions = options.map((o) => ({ ...o, selected: selecting }));
    const newSelected = selecting ? options.map((o) => o.value) : [];
    notify(newSelected, newOptions);
  };

  return (
    <div className="relative">
      <select className="hidden" id={id}>
        {options.map((o, i) => (
          <option key={o.value} id={String(i)} value={o.value}>
            {o.text}
          </option>
        ))}
      </select>

      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={() => setShow((s) => !s)}
        className={`${inputClass} flex cursor-pointer`}
      >
        <div className="flex min-h-0 flex-auto flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className="text-xs font-semibold text-gray-400">
              {placeholder}
            </span>
          ) : (
            selected.map((val) => {
              const option = options.find((o) => o.value === val);
              if (!option) return null;
              return (
                <span
                  key={option.value}
                  className="border-stroke bg-gray dark:border-strokedark flex items-center gap-1 rounded border-[.5px] px-1.5 py-0.5 text-xs font-semibold dark:bg-white/30"
                >
                  {option.text}
                  <i
                    onClick={(e) => remove(e, options.indexOf(option))}
                    className="fas fa-times-circle hover:text-danger cursor-pointer text-[10px]"
                  />
                </span>
              );
            })
          )}
        </div>
        <button
          type="button"
          className="ml-1 flex w-4 items-center justify-center"
        >
          <i className="fas fa-chevron-down text-xs" />
        </button>
      </div>

      {/* Dropdown */}
      {show && (
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          className="dark:bg-form-input z-[9999] overflow-hidden rounded bg-white shadow-lg"
        >
          {/* Search */}
          <input
            type="text"
            placeholder="Търсене..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-primary w-full border-b px-2 py-1.5 text-xs font-semibold text-gray-900 outline-none dark:text-white"
          />

          {/* Select all */}
          <button
            type="button"
            onClick={toggleSelectAll}
            className={`border-stroke flex w-full items-center justify-between border-b px-3 py-1.5 text-xs font-semibold tracking-wide transition-colors
              ${
                allSelected
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-primary/5 bg-gray-50 text-gray-900 dark:bg-white/5 dark:text-white"
              }`}
          >
            <span className="flex items-center gap-1.5">
              <i
                className={`fas fa-${
                  allSelected ? "check-square" : "square"
                } text-xs`}
              />
              Избери всички
            </span>
            <span className="text-[10px] font-normal opacity-60">
              {selected.length}/{options.length}
            </span>
          </button>

          {/* Options list */}
          <ul className="max-h-40 overflow-y-auto">
            {filteredOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => toggle(options.indexOf(option))}
                className="border-stroke hover:bg-primary/5 dark:border-form-strokedark flex cursor-pointer items-center justify-between border-b px-3 py-1.5 text-xs font-semibold text-gray-900 dark:text-white"
              >
                {option.text}
                {option.selected && (
                  <i className="fa fa-check text-primary text-[10px]" />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
